<template>
  <div
    id="meshWrapper"
    ref="meshWrapper"
    class="mesh-wrapper"
    :class="{'tourActive': tourCurrentStep >= 0 && (tourCurrentTarget === '#meshWrapper')}"
    @mousemove="handleMouseMovement"
    @mouseleave="handleMouseLeave"
    @mouseenter="handleMouseEnter"
  >
    <img
      v-if="!isTouchDevice"
      src="../assets/rotationHelper.svg"
      alt=""
      class="roation-helper"
    >
    <transition name="fade">
      <div
        v-if="mouseHint && !isTouchDevice"
        class="mouse-hint"
      >
        <img
          src="../assets/mouseMoveArrow.svg"
          alt=""
        >
      </div>
    </transition>
    <canvas
      id="meshCanvas"
      ref="meshCanvas"
    />
  </div>
</template>

<script>
import '@/vendor/STLLoader.js'
import * as THREE from 'three'
import Level from '@/lib/level'
import { trackballControlFactory } from 'ami.js'
const loaderSTL = new THREE.STLLoader()
export default {
  name: 'MeshView',
  props: {
    isTouchDevice: {
      type: Boolean,
      default: undefined
    },
    tourCurrentStep: {
      type: Number,
      default: undefined
    },
    tourCurrentTarget: {
      type: String,
      default: undefined
    }
  },
  data: function () {
    return {
      canvas: undefined,
      wrapper: undefined,

      canvasSizeX: 0,
      canvasSizeY: 0,
      wrapperOffsetX: 0,
      wrapperOffsetY: 0,

      mouseHint: false,
      mouseHintTimer: undefined,

      mouseX: 0,
      mouseY: 0,

      VIEW_ANGLE: 45,
      NEAR: 0.1,
      FAR: 10000,

      camera: undefined,
      renderer: undefined,
      scene: undefined,
      model: {},
      controls: undefined,

      sondeModelPath: '/SondeModelS.stl',
      sliceLinePath: '/sliceLine.stl',
      spherePath: '/sphere.stl',
      cylinderPath: '/cylinder.stl',
      cubePath: '/cube.stl',
      slicePath: '/slice.stl',
      questionmarkTexture: '/questionmarkTexture.png'
    }
  },
  mounted () {
    this.canvas = this.$refs.meshCanvas
    this.wrapper = this.$refs.meshWrapper

    this.canvasSizeX = this.wrapper.offsetWidth
    this.canvasSizeY = this.wrapper.offsetHeight
    this.wrapperOffsetX = this.wrapper.getBoundingClientRect().left
    this.wrapperOffsetY = this.wrapper.getBoundingClientRect().top
    this.setup()
    const ControlsTrackball = trackballControlFactory(THREE)
    this.controls = new ControlsTrackball(this.camera, this.canvas)

    this.handleMouseLeave()
  },
  methods: {
    setup () {
      this.camera = new THREE.PerspectiveCamera(this.VIEW_ANGLE, this.canvasSizeX / this.canvasSizeY, this.NEAR, this.FAR)

      this.camera.position.set(50, 75, (5000 / this.canvasSizeY) + 250)

      this.renderer = new THREE.WebGLRenderer({
        antialias: true,
        canvas: this.canvas
      })
      this.scene = new THREE.Scene()
      this.scene.add(this.camera)
      this.renderer.setSize(this.canvasSizeX, this.canvasSizeY)
      this.renderer.setClearColor(0x121339, 1)
      this.addElements()
      if (Level[this.$store.state.currentGame].name === 'Tutorial') {
        this.addGeometries()
      } else {
        this.addCoverSphere()
      }
    },
    loadLevel () {
      if (Level[this.$store.state.currentGame].name === 'Tutorial') {
        this.addGeometries()
        this.scene.remove(this.model.questionmarSphere)
      } else {
        this.addCoverSphere()
        this.scene.remove(this.model.sphere)
        this.scene.remove(this.model.cube)
        this.scene.remove(this.model.cylinder)
      }
    },
    addElements () {
      loaderSTL.load(this.sondeModelPath, (object) => {
        // smooth shading von stack overflow: https://stackoverflow.com/questions/35136282/how-to-smooth-mesh-triangles-in-stl-loaded-buffergeometry
        /// ////////////////////////////////////////////////////////////

        var attrib = object.getAttribute('position')
        if (attrib === undefined) {
          throw new Error('a given BufferGeometry object must have a position attribute.')
        }
        var positions = attrib.array
        var vertices = []
        for (var i = 0, n = positions.length; i < n; i += 3) {
          var x = positions[i]
          var y = positions[i + 1]
          var z = positions[i + 2]
          vertices.push(new THREE.Vector3(x, y, z))
        }
        var faces = []
        for (var i = 0, n = vertices.length; i < n; i += 3) {
          faces.push(new THREE.Face3(i, i + 1, i + 2))
        }

        var geometry = new THREE.Geometry()
        geometry.vertices = vertices
        geometry.faces = faces
        geometry.computeFaceNormals()
        geometry.mergeVertices()
        geometry.computeVertexNormals()

        /// ////////////////////////////////////////////////////////////
        var material = new THREE.MeshLambertMaterial({ color: 0xffffff })
        var mesh = new THREE.Mesh(geometry, material)

        this.model.sonde = mesh
        this.scene.add(this.model.sonde)
      })

      loaderSTL.load(this.slicePath, geometry => {
        var material = new THREE.MeshLambertMaterial({ color: 0x000000 })
        var mesh = new THREE.Mesh(geometry, material)
        this.model.slice = mesh
        this.scene.add(this.model.slice)
      })

      loaderSTL.load(this.sliceLinePath, geometry => {
        var material = new THREE.MeshLambertMaterial({ color: 0x34ebeb })
        var mesh = new THREE.Mesh(geometry, material)
        this.model.sliceLine = mesh
        this.scene.add(this.model.sliceLine)
      })
      this.scene.add(new THREE.AmbientLight(0xeeeeee))
    },
    addCoverSphere () {
      var questionmarkMaterial = new THREE.MeshLambertMaterial()
      questionmarkMaterial.map = THREE.ImageUtils.loadTexture(this.questionmarkTexture)
      questionmarkMaterial.shininess = 10
      var questionmarkmesh = new THREE.Mesh(new THREE.SphereGeometry(50, 10, 10), questionmarkMaterial)
      questionmarkmesh.rotation.y = -Math.PI / 2

      this.model.questionmarSphere = questionmarkmesh
      this.model.questionmarSphere.position.set(50, 50, 50)
      this.scene.add(this.model.questionmarSphere)
    },
    addGeometries () {
      loaderSTL.load(this.cubePath, geometry => {
        var material = new THREE.MeshLambertMaterial({ color: 0x970064 })
        var mesh = new THREE.Mesh(geometry, material)
        mesh.rotation.y = Math.PI / 2
        this.model.cube = mesh
        this.scene.add(this.model.cube)
      })
      loaderSTL.load(this.cylinderPath, geometry => {
        var material = new THREE.MeshLambertMaterial({ color: 0x009764 })
        var mesh = new THREE.Mesh(geometry, material)
        mesh.rotation.y = Math.PI / 2
        this.model.cylinder = mesh
        this.scene.add(this.model.cylinder)
      })
      loaderSTL.load(this.spherePath, geometry => {
        var material = new THREE.MeshLambertMaterial({ color: 0x988E65 })
        var mesh = new THREE.Mesh(geometry, material)
        mesh.rotation.y = Math.PI / 2
        this.model.sphere = mesh
        this.scene.add(this.model.sphere)
      })
    },
    handleMouseMovement (event) {
      this.mouseX = event.clientX
      this.$store.state.mouseXRelative = (100 / this.canvasSizeX * (this.mouseX - this.wrapperOffsetX)) / 100
      this.mouseY = event.clientY
      this.$store.state.mouseYRelative = (100 / this.canvasSizeY * (this.mouseY - this.wrapperOffsetY)) / 100
    },
    handleMouseLeave (event) {
      this.mouseHintTimer = setTimeout(() => {
        this.mouseHint = true
      }, 8000)
    },
    handleMouseEnter (event) {
      clearTimeout(this.mouseHintTimer)
      this.mouseHint = false
    },
    setSize () {
      this.renderer.setSize(0, 0)
      this.canvasSizeX = this.wrapper.offsetWidth
      this.canvasSizeY = this.wrapper.offsetHeight - 6
      this.renderer.setSize(this.canvasSizeX, this.canvasSizeY)
      this.camera.aspect = this.canvasSizeX / this.canvasSizeY
      this.camera.updateProjectionMatrix()
    },
    render () {
      // if (this.controls) {
      //   this.controls.update()
      // }
      if (this.$store.state.stackCenter) {
        this.$store.commit('setProbePosition',
          new THREE.Vector3(
            (this.$store.state.mouseXRelative) * this.$store.state.stackCenter.x * 2,
            this.$store.state.stackCenter.y * 2,
            (this.$store.state.mouseYRelative) * this.$store.state.stackCenter.z * 1.9))
      }

      if (this.$store.state.probePosition && this.$store.state.planeDirection) {
        // #sonde --------------------------------------------------------------------------------------------------------
        if (this.model.sonde) {
          this.model.sonde.scale.set(0.5, 0.5, 0.5)
          this.model.sonde.position.set(this.$store.state.probePosition.x, this.$store.state.probePosition.y, this.$store.state.probePosition.z)
          this.model.sonde.lookAt(
            this.$store.state.probePosition.x + this.$store.state.planeDirection.x,
            this.$store.state.probePosition.y + this.$store.state.planeDirection.y,
            this.$store.state.probePosition.z + this.$store.state.planeDirection.z
          )
        }
        // #slice --------------------------------------------------------------------------------------------------------
        if (this.model.slice) {
          this.model.slice.position.set(this.$store.state.probePosition.x, this.$store.state.probePosition.y, this.$store.state.probePosition.z)
          this.model.slice.scale.set(35, 35, 35)
          this.model.slice.lookAt(
            this.$store.state.probePosition.x + this.$store.state.planeDirection.x,
            this.$store.state.probePosition.y + this.$store.state.planeDirection.y,
            this.$store.state.probePosition.z + this.$store.state.planeDirection.z
          )
        }
        // #sliceLine --------------------------------------------------------------------------------------------------------
        if (this.model.sliceLine) {
          this.model.sliceLine.position.set(this.$store.state.probePosition.x, this.$store.state.probePosition.y, this.$store.state.probePosition.z)
          this.model.sliceLine.scale.set(35, 35, 35)
          this.model.sliceLine.lookAt(
            this.$store.state.probePosition.x + this.$store.state.planeDirection.x,
            this.$store.state.probePosition.y + this.$store.state.planeDirection.y,
            this.$store.state.probePosition.z + this.$store.state.planeDirection.z
          )
        }
      }
      if (this.model.cube && this.model.cylinder && this.model.sphere && (Level[this.$store.state.currentGame].rounds[this.$store.state.currentRound].solutionType === '3DVIEW' || Level[this.$store.state.currentGame].rounds[this.$store.state.currentRound].solutionType === 'NONE')) {
        // // #geometries --------------------------------------------------------------------------------------------------------
        this.model.cube.scale.set(
          Level[this.$store.state.currentGame].rounds[this.$store.state.currentRound].correctSolution.cube.scale,
          Level[this.$store.state.currentGame].rounds[this.$store.state.currentRound].correctSolution.cube.scale,
          Level[this.$store.state.currentGame].rounds[this.$store.state.currentRound].correctSolution.cube.scale
        )
        this.model.cube.position.set(Level[this.$store.state.currentGame].rounds[this.$store.state.currentRound].correctSolution.cube.x, Level[this.$store.state.currentGame].rounds[this.$store.state.currentRound].correctSolution.cube.y, Level[this.$store.state.currentGame].rounds[this.$store.state.currentRound].correctSolution.cube.z)

        this.model.cylinder.scale.set(Level[this.$store.state.currentGame].rounds[this.$store.state.currentRound].correctSolution.cylinder.scale, Level[this.$store.state.currentGame].rounds[this.$store.state.currentRound].correctSolution.cylinder.scale, Level[this.$store.state.currentGame].rounds[this.$store.state.currentRound].correctSolution.cylinder.scale)
        this.model.cylinder.position.set(Level[this.$store.state.currentGame].rounds[this.$store.state.currentRound].correctSolution.cylinder.x, Level[this.$store.state.currentGame].rounds[this.$store.state.currentRound].correctSolution.cylinder.y, Level[this.$store.state.currentGame].rounds[this.$store.state.currentRound].correctSolution.cylinder.z)

        this.model.sphere.scale.set(Level[this.$store.state.currentGame].rounds[this.$store.state.currentRound].correctSolution.sphere.scale, Level[this.$store.state.currentGame].rounds[this.$store.state.currentRound].correctSolution.sphere.scale, Level[this.$store.state.currentGame].rounds[this.$store.state.currentRound].correctSolution.sphere.scale)
        this.model.sphere.position.set(
          Level[this.$store.state.currentGame].rounds[this.$store.state.currentRound].correctSolution.sphere.x,
          Level[this.$store.state.currentGame].rounds[this.$store.state.currentRound].correctSolution.sphere.y,
          Level[this.$store.state.currentGame].rounds[this.$store.state.currentRound].correctSolution.sphere.z
        )
      }
      if (this.$store.state.stackCenter) {
        this.camera.lookAt(this.$store.state.stackCenter.x, this.$store.state.stackCenter.y + 25, this.$store.state.stackCenter.z)
      }
      this.renderer.render(this.scene, this.camera)
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/style.scss';
.mesh-wrapper{
  grid-area: mesh;
  position: relative;
  .roation-helper{
    position: absolute;
    right: 15px;
    top: 15px;
    width: 100px;
  }
  .mouse-hint{
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    background-color: rgba($color: $cyan, $alpha: 0.5);
    img{
      width: 200px;
    }
  }
  canvas{
    width: 0;
  }
}
</style>
